import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, Trash2, RefreshCw, Copy, Check, X, Radio, Tv, RotateCcw, Pencil, Braces } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  createPhone,
  deletePhone,
  listPhones,
  rebootPhone,
  updatePhone,
  type Phone,
  type PhoneCreateResponse,
} from "../api/phones";
import { StatusBadge } from "../components/StatusBadge";
import { relativeTime, formatDateTime } from "../utils/format";
import { type RealtimeEvent, useRealtimeChannel } from "../hooks/useRealtimeChannel";

export function PhonesPage(): JSX.Element {
  const { t } = useTranslation();
  const [phones, setPhones] = useState<Phone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [credsModal, setCredsModal] = useState<PhoneCreateResponse | null>(null);
  const [envPhone, setEnvPhone] = useState<Phone | null>(null);

  async function refresh() {
    try {
      setError(null);
      const data = await listPhones();
      setPhones(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // Fallback long : refresh complet toutes les 60s pour récupérer
    // les phones créés/supprimés (ces events ne sont pas encore broadcast).
    const iv = setInterval(() => void refresh(), 60_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Realtime : status + last_heartbeat_at par tel
  const rooms = useMemo(() => ["phones"], []);
  const onEvent = useCallback((evt: RealtimeEvent) => {
    if (evt.event !== "phone:update") return;
    const p = evt.payload;
    setPhones((cur) =>
      cur.map((ph) =>
        ph.id === p.phone_id
          ? {
              ...ph,
              status: p.status ?? ph.status,
              // Le backend porte computed_status (peut valoir 'paused'). On le
              // privilégie ; fallback sur status brut pour les vieux events.
              computed_status: (p.computed_status ?? p.status ?? ph.computed_status) as Phone["computed_status"],
              last_heartbeat_at: p.last_heartbeat_at ?? ph.last_heartbeat_at,
              os_version: p.os_version ?? ph.os_version,
              model: p.model ?? ph.model,
              spoofed_model: p.spoofed_model ?? ph.spoofed_model,
            }
          : ph
      )
    );
  }, []);
  const { connected } = useRealtimeChannel(rooms, onEvent);

  async function handleDelete(phone: Phone) {
    if (!window.confirm(t("phones.confirmDelete"))) {
      return;
    }
    try {
      await deletePhone(phone.id);
      await refresh();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : t("common.error"));
    }
  }

  async function handleRename(phone: Phone, newName: string) {
    const name = newName.trim();
    if (!name || name === phone.name) return;
    const prev = phone.name;
    setPhones((cur) => cur.map((ph) => (ph.id === phone.id ? { ...ph, name } : ph)));
    try {
      await updatePhone(phone.id, { name });
    } catch (err) {
      setPhones((cur) => cur.map((ph) => (ph.id === phone.id ? { ...ph, name: prev } : ph)));
      window.alert(err instanceof Error ? err.message : t("common.error"));
    }
  }

  async function handleReboot(phone: Phone) {
    if (!window.confirm(t("phones.actions.reboot") + " " + phone.name + " ?")) {
      return;
    }
    try {
      await rebootPhone(phone.id);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : t("common.error"));
    }
  }

  return (
    <div className="p-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{t("phones.title")}</h1>
          <p className="mt-1 text-ink-500 text-sm">
            {t("phones.enrolledCount", { count: phones.length })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
              connected
                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                : "bg-ink-700/40 text-ink-500 border-ink-600"
            }`}
          >
            <Radio className={`size-3 ${connected ? "animate-pulse" : ""}`} />
            {connected ? t("jobs.detail.live") : t("jobs.detail.offline")}
          </div>
          <button
            onClick={() => void refresh()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-700 hover:border-ink-600 text-sm text-ink-500 hover:text-white"
            title={t("common.refresh")}
          >
            <RefreshCw className="size-4" />
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium transition-colors"
          >
            <Plus className="size-4" />
            {t("phones.addBtn")}
          </button>
        </div>
      </header>

      {error && (
        <div className="mb-6 p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
          {error}
        </div>
      )}

      <div className="bg-ink-800 border border-ink-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-700/50 text-ink-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">{t("phones.columns.name")}</th>
              <th className="text-left px-4 py-3 font-semibold">{t("phones.columns.status")}</th>
              <th className="text-left px-4 py-3 font-semibold">{t("phones.columns.model")}</th>
              <th className="text-left px-4 py-3 font-semibold">{t("phones.columns.url")}</th>
              <th className="text-left px-4 py-3 font-semibold">{t("phones.columns.lastHeartbeat")}</th>
              <th className="text-right px-4 py-3 font-semibold">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-700">
            {loading && phones.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-500">
                  {t("common.loading")}
                </td>
              </tr>
            )}
            {!loading && phones.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-ink-500">
                  {t("phones.empty")}
                </td>
              </tr>
            )}
            {phones.map((p) => (
              <tr key={p.id} className="hover:bg-ink-700/30 transition-colors">
                <td className="px-4 py-3 font-medium">
                  <EditableName
                    value={p.name}
                    onSave={(newName) => void handleRename(p, newName)}
                  />
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={p.computed_status} />
                </td>
                <td className="px-4 py-3 text-ink-500">
                  {p.model ?? "—"}
                  {p.spoofed_model && (
                    <span className="text-ink-600"> → {p.spoofed_model}</span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-ink-500 truncate max-w-xs">
                  {p.connection_url}
                </td>
                <td className="px-4 py-3 text-ink-500" title={formatDateTime(p.last_heartbeat_at)}>
                  {relativeTime(p.last_heartbeat_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center justify-end gap-1">
                    <Link
                      to={`/phones/${p.id}/stream`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-sky-400 hover:text-sky-300 hover:bg-sky-500/10"
                      title={t("phones.actions.stream")}
                    >
                      <Tv className="size-4" />
                    </Link>
                    <button
                      onClick={() => setEnvPhone(p)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-violet-400 hover:text-violet-300 hover:bg-violet-500/10"
                      title={t("phones.actions.envVars")}
                    >
                      <Braces className="size-4" />
                    </button>
                    <button
                      onClick={() => void handleReboot(p)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                      title={t("phones.actions.reboot")}
                    >
                      <RotateCcw className="size-4" />
                    </button>
                    <button
                      onClick={() => void handleDelete(p)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      title={t("common.delete")}
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreatePhoneModal
          onClose={() => setShowCreate(false)}
          onCreated={(resp) => {
            setShowCreate(false);
            setCredsModal(resp);
            void refresh();
          }}
        />
      )}

      {credsModal && (
        <CredentialsModal data={credsModal} onClose={() => setCredsModal(null)} />
      )}

      {envPhone && (
        <EnvVarsModal
          phone={envPhone}
          onClose={() => setEnvPhone(null)}
          onSaved={(updated) => {
            setPhones((cur) => cur.map((ph) => (ph.id === updated.id ? updated : ph)));
            setEnvPhone(null);
          }}
        />
      )}
    </div>
  );
}

function EditableName({
  value,
  onSave,
}: {
  value: string;
  onSave: (newValue: string) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [editing, value]);

  function commit() {
    setEditing(false);
    onSave(draft);
  }

  function cancel() {
    setEditing(false);
    setDraft(value);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
        className="w-full px-2 py-1 rounded bg-ink-900 border border-velvet-500 focus:outline-none text-white text-sm font-medium"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group inline-flex items-center gap-1.5 text-left hover:text-velvet-300 transition-colors"
      title="Cliquer pour renommer"
    >
      <span>{value}</span>
      <Pencil className="size-3 text-ink-600 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

// ---------------------------------------------------------------------------
// EnvVarsModal — édition du .env du téléphone (injecté dans tous les script
// runs comme `params`). Deux vues synchronisées sur le même état dict :
//   - Grille clé/valeur : ajout/suppression de lignes, idéal pour quelques
//     variables sans risque de syntaxe.
//   - Texte format `.env` : `KEY=value` par ligne, lignes vides et `#…`
//     ignorées. Pratique pour copier-coller depuis ailleurs.
// La sauvegarde envoie le dict via PATCH /api/phones/:id { env_vars }.
// ---------------------------------------------------------------------------

type EnvRow = { id: number; key: string; value: string };

let envRowSeq = 0;
const newRowId = () => ++envRowSeq;

function dictToRows(dict: Record<string, string>): EnvRow[] {
  return Object.entries(dict).map(([key, value]) => ({ id: newRowId(), key, value }));
}

function rowsToDict(rows: EnvRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const k = r.key.trim();
    if (k.length === 0) continue;
    out[k] = r.value;
  }
  return out;
}

function dictToEnvText(dict: Record<string, string>): string {
  return Object.entries(dict)
    .map(([k, v]) => `${k}=${v}`)
    .join("\n");
}

function envTextToDict(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (line.length === 0 || line.startsWith("#")) continue;
    const eq = line.indexOf("=");
    if (eq < 0) continue;
    const key = line.slice(0, eq).trim();
    if (key.length === 0) continue;
    let value = line.slice(eq + 1);
    // Strip un éventuel guillemet englobant (très tolérant, on déguillemette
    // sans interpréter d'échappements).
    if (
      (value.startsWith('"') && value.endsWith('"') && value.length >= 2) ||
      (value.startsWith("'") && value.endsWith("'") && value.length >= 2)
    ) {
      value = value.slice(1, -1);
    }
    out[key] = value;
  }
  return out;
}

function EnvVarsModal({
  phone,
  onClose,
  onSaved,
}: {
  phone: Phone;
  onClose: () => void;
  onSaved: (updated: Phone) => void;
}): JSX.Element {
  const [view, setView] = useState<"grid" | "text">("grid");
  const [rows, setRows] = useState<EnvRow[]>(() => dictToRows(phone.env_vars || {}));
  const [text, setText] = useState<string>(() => dictToEnvText(phone.env_vars || {}));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bascule grille → texte : on sérialise le state grille en .env.
  // Bascule texte → grille : on parse le texte, on perd les éventuels lignes
  // mal formées (qu'on signale à l'utilisateur si on en détecte).
  function switchTo(newView: "grid" | "text") {
    if (newView === view) return;
    if (newView === "text") {
      setText(dictToEnvText(rowsToDict(rows)));
    } else {
      setRows(dictToRows(envTextToDict(text)));
    }
    setView(newView);
  }

  function updateRow(id: number, patch: Partial<EnvRow>) {
    setRows((cur) => cur.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((cur) => [...cur, { id: newRowId(), key: "", value: "" }]);
  }
  function removeRow(id: number) {
    setRows((cur) => cur.filter((r) => r.id !== id));
  }

  async function handleSave() {
    setSubmitting(true);
    setError(null);
    try {
      const dict = view === "grid" ? rowsToDict(rows) : envTextToDict(text);
      const updated = await updatePhone(phone.id, { env_vars: dict });
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sauvegarde échouée");
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title={`Variables — ${phone.name}`}>
      <div className="space-y-4">
        <p className="text-xs text-ink-500">
          Ces variables sont injectées dans tous les runs de script lancés sur ce
          téléphone, accessibles via <code>${"${KEY}"}</code> dans le DSL. Les
          paramètres explicites d'un job (si présents) surchargent ces valeurs.
        </p>

        <div className="flex items-center gap-1 border border-ink-700 rounded-lg p-1 w-fit">
          <button
            type="button"
            onClick={() => switchTo("grid")}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              view === "grid" ? "bg-velvet-600 text-white" : "text-ink-500 hover:text-white"
            }`}
          >
            Grille
          </button>
          <button
            type="button"
            onClick={() => switchTo("text")}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              view === "text" ? "bg-velvet-600 text-white" : "text-ink-500 hover:text-white"
            }`}
          >
            Texte (.env)
          </button>
        </div>

        {view === "grid" ? (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {rows.length === 0 && (
              <div className="text-sm text-ink-600 italic">
                Aucune variable. Ajoute une ligne pour commencer.
              </div>
            )}
            {rows.map((r) => (
              <div key={r.id} className="flex items-center gap-2">
                <input
                  type="text"
                  value={r.key}
                  onChange={(e) => updateRow(r.id, { key: e.target.value })}
                  placeholder="KEY"
                  className="w-1/3 px-3 py-1.5 rounded bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-white text-sm font-mono"
                />
                <span className="text-ink-600 select-none">=</span>
                <input
                  type="text"
                  value={r.value}
                  onChange={(e) => updateRow(r.id, { value: e.target.value })}
                  placeholder="value"
                  className="flex-1 px-3 py-1.5 rounded bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-white text-sm font-mono"
                />
                <button
                  type="button"
                  onClick={() => removeRow(r.id)}
                  className="p-1.5 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  title="Supprimer cette ligne"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addRow}
              className="inline-flex items-center gap-1 px-3 py-1.5 mt-1 rounded text-velvet-300 hover:text-velvet-200 hover:bg-velvet-500/10 text-sm"
            >
              <Plus className="size-4" /> Ajouter une variable
            </button>
          </div>
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`PROXY_REBOOT_URL=https://gridpanel.net/api/reboot?token=…\nGRID_TOKEN=...`}
            spellCheck={false}
            rows={12}
            className="w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-white text-sm font-mono"
          />
        )}

        {error && (
          <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-ink-500 hover:text-white"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {submitting ? "Sauvegarde…" : "Sauvegarder"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CreatePhoneModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (resp: PhoneCreateResponse) => void;
}): JSX.Element {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [spoofed, setSpoofed] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const resp = await createPhone({
        name: name.trim(),
        connection_url: url.trim(),
        spoofed_model: spoofed.trim() || undefined,
      });
      onCreated(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Création échouée");
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} title="Ajouter un téléphone">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Nom d'affichage" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="iPhone 8 - rouge"
            required
            className="w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-white placeholder:text-ink-600"
          />
        </Field>

        <Field
          label="Connection URL"
          required
          hint="https://<ip_publique_box>:<port_externe>"
        >
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://82.66.99.99:8443"
            required
            className="w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-white placeholder:text-ink-600 font-mono text-sm"
          />
        </Field>

        <Field label="Modèle spoofé (optionnel)" hint="ex: iPhone14,8">
          <input
            type="text"
            value={spoofed}
            onChange={(e) => setSpoofed(e.target.value)}
            placeholder="iPhone14,8"
            className="w-full px-3 py-2 rounded-lg bg-ink-900 border border-ink-700 focus:border-velvet-500 focus:outline-none text-white placeholder:text-ink-600 font-mono text-sm"
          />
        </Field>

        {error && (
          <div className="p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-ink-500 hover:text-white"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {submitting ? "Création…" : "Créer & générer credentials"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function CredentialsModal({
  data,
  onClose,
}: {
  data: PhoneCreateResponse;
  onClose: () => void;
}): JSX.Element {
  return (
    <Modal onClose={onClose} title="Téléphone créé — enrôlement">
      <div className="space-y-4">
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm">
          ⚠️ L'<code>api_token</code> ci-dessous n'est affiché qu'<strong>une seule fois</strong>.
          Copie-le immédiatement.
        </div>

        <CopyField label="device_id" value={data.credentials.device_id} mono />
        <CopyField label="api_token" value={data.credentials.api_token} mono secret />

        <div>
          <div className="text-xs text-ink-500 uppercase tracking-wider font-semibold mb-2">
            Commande SSH d'enrôlement
          </div>
          <CopyField multiline value={data.enrollment_command} mono />
          <p className="mt-2 text-xs text-ink-500">
            Remplace <code>&lt;ip_tel&gt;</code> par l'IP locale de l'iPhone. La valeur{" "}
            <code>dashboardWhitelistIP</code> est l'IP du hub vue par l'agent.
          </p>
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-velvet-600 hover:bg-velvet-500 text-white text-sm font-medium"
          >
            J'ai sauvegardé le token
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal({
  children,
  onClose,
  title,
}: {
  children: React.ReactNode;
  onClose: () => void;
  title: string;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-ink-800 border border-ink-700 rounded-2xl max-w-2xl w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="text-ink-500 hover:text-white">
            <X className="size-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-semibold text-ink-500 uppercase tracking-wider mb-1.5">
        {label} {required && <span className="text-velvet-400">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-ink-600">{hint}</p>}
    </div>
  );
}

function CopyField({
  label,
  value,
  mono,
  secret,
  multiline,
}: {
  label?: string;
  value: string;
  mono?: boolean;
  secret?: boolean;
  multiline?: boolean;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const [reveal, setReveal] = useState(!secret);
  const displayed = reveal ? value : "•".repeat(Math.min(value.length, 32));

  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div>
      {label && (
        <div className="text-xs text-ink-500 uppercase tracking-wider font-semibold mb-1.5">
          {label}
        </div>
      )}
      <div
        className={`relative bg-ink-900 border border-ink-700 rounded-lg p-3 ${
          mono ? "font-mono text-xs" : "text-sm"
        } ${multiline ? "whitespace-pre" : "truncate"}`}
      >
        {displayed}
        <div className="absolute top-2 right-2 flex gap-1">
          {secret && (
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              className="px-2 py-0.5 rounded text-xs bg-ink-700 hover:bg-ink-600 text-ink-300"
            >
              {reveal ? "Cacher" : "Voir"}
            </button>
          )}
          <button
            type="button"
            onClick={() => void copy()}
            className="px-2 py-0.5 rounded text-xs bg-ink-700 hover:bg-ink-600 text-ink-300 inline-flex items-center gap-1"
          >
            {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
            {copied ? "Copié" : "Copier"}
          </button>
        </div>
      </div>
    </div>
  );
}
